import React, { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const WHATSAPP_NUMBER = "919141302057";

const businessTypes = [
  "Kirana / Grocery",
  "Restaurant / Café",
  "CSC Center",
  "Salon / Beauty",
  "Boutique / Clothing",
  "Electronics / Mobile Shop",
  "Service Provider",
  "Other",
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Indian mobile: 10 digits starting 6-9 (with optional +91/0 prefix stripped before check)
const normalisePhone = (raw) => (raw || "").replace(/\D+/g, "").replace(/^91/, "").replace(/^0/, "");
const isValidPhone = (raw) => /^[6-9]\d{9}$/.test(normalisePhone(raw));

export default function LeadForm({ formRef }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    business_type: "Kirana / Grocery",
    message: "",
  });
  const [errors, setErrors] = useState({});

  const onChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) setErrors((er) => ({ ...er, [e.target.name]: "" }));
  };

  const validate = () => {
    const er = {};
    if (!form.name.trim() || form.name.trim().length < 2) er.name = "Please enter your full name.";
    if (!form.email.trim()) er.email = "Email is required.";
    else if (!EMAIL_RE.test(form.email.trim())) er.email = "Please enter a valid email address.";
    if (!form.phone.trim()) er.phone = "WhatsApp number is required.";
    else if (!isValidPhone(form.phone)) er.phone = "Enter a valid 10-digit Indian mobile number.";
    if (!form.business_type) er.business_type = "Please choose a business type.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }

    const cleanPhone = normalisePhone(form.phone);
    const text =
      `Namaste 🙏 I'd like a free Digital Onboarding consultation.\n\n` +
      `*Name:* ${form.name}\n` +
      `*Email:* ${form.email}\n` +
      `*WhatsApp:* +91 ${cleanPhone}\n` +
      `*Business Type:* ${form.business_type}` +
      (form.message ? `\n*About my business:* ${form.message}` : "");

    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;

    toast.success("Redirecting to WhatsApp…");
    if (typeof window !== "undefined") {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const inputClass = (field) =>
    `mt-2 w-full bg-transparent border ${
      errors[field] ? "border-rose-500/70" : "border-white/10"
    } focus:border-violet-500/60 focus:outline-none rounded-xl px-4 py-3 text-white placeholder:text-slate-600 transition-colors`;

  return (
    <section
      id="get-started"
      data-testid="lead-form-section"
      ref={formRef}
      className="relative py-24 lg:py-32 overflow-hidden"
    >
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[70%] radial-glow-violet blur-3xl opacity-60" />

      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-2"
        >
          <p className="uppercase text-xs tracking-[0.24em] text-violet-300 font-semibold">
            Start earning online
          </p>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Your shop,{" "}
            <span className="text-gradient-neon">online in 2 days, ₹9,999.</span>
          </h2>
          <p className="mt-5 text-slate-400 leading-relaxed max-w-md">
            Share a few details and our onboarding specialist will continue the chat on
            WhatsApp instantly — with all your info pre-filled.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-slate-300">
            {[
              "Free consultation in Hindi or English",
              "Website + App + Payments in one package",
              "Dedicated support on WhatsApp",
            ].map((b) => (
              <li key={b} className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-violet-300 shrink-0" />
                {b}
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          onSubmit={submit}
          noValidate
          data-testid="lead-form"
          className="lg:col-span-3 rounded-3xl glass-strong border border-white/5 p-6 sm:p-8 shadow-2xl"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Full name</label>
              <input
                data-testid="lead-input-name"
                name="name"
                value={form.name}
                onChange={onChange}
                placeholder="Your full name"
                className={inputClass("name")}
              />
              {errors.name && (
                <p data-testid="lead-error-name" className="mt-1.5 text-xs text-rose-400">{errors.name}</p>
              )}
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Email</label>
              <input
                data-testid="lead-input-email"
                type="email"
                name="email"
                value={form.email}
                onChange={onChange}
                placeholder="you@business.com"
                className={inputClass("email")}
              />
              {errors.email && (
                <p data-testid="lead-error-email" className="mt-1.5 text-xs text-rose-400">{errors.email}</p>
              )}
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">WhatsApp Number</label>
              <input
                data-testid="lead-input-phone"
                name="phone"
                inputMode="tel"
                value={form.phone}
                onChange={onChange}
                placeholder="10-digit mobile, e.g. 98xxxxxxxx"
                className={inputClass("phone")}
              />
              {errors.phone && (
                <p data-testid="lead-error-phone" className="mt-1.5 text-xs text-rose-400">{errors.phone}</p>
              )}
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Business type</label>
              <select
                data-testid="lead-input-business-type"
                name="business_type"
                value={form.business_type}
                onChange={onChange}
                className={`mt-2 w-full bg-[#0b0c1d] border ${
                  errors.business_type ? "border-rose-500/70" : "border-white/10"
                } focus:border-violet-500/60 focus:outline-none rounded-xl px-4 py-3 text-white transition-colors`}
              >
                {businessTypes.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-widest text-slate-500">
                Tell us about your business (optional)
              </label>
              <textarea
                data-testid="lead-input-message"
                name="message"
                value={form.message}
                onChange={onChange}
                rows={3}
                placeholder="Shop name, city, what you sell…"
                className="mt-2 w-full bg-transparent border border-white/10 focus:border-violet-500/60 focus:outline-none rounded-xl px-4 py-3 text-white placeholder:text-slate-600 transition-colors resize-none"
              />
            </div>
          </div>

          <button
            type="submit"
            data-testid="lead-form-submit"
            className="mt-6 w-full group px-6 py-3.5 rounded-xl btn-primary-grad text-white font-semibold text-base transition-all hover:-translate-y-0.5 shadow-xl shadow-violet-500/30 flex items-center justify-center gap-2"
          >
            Request Free Consultation
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>

          <p className="mt-3 text-center text-xs text-slate-500">
            We&apos;ll open WhatsApp with your details pre-filled — no extra typing needed.
          </p>
        </motion.form>
      </div>
    </section>
  );
}
